import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle, XCircle, AlertTriangle, TrendingUp, BookOpen, Target, Sparkles, ArrowRight } from "lucide-react";
import { useTranslation} from "react-i18next";

interface ATSAnalysis {
  score: number;
  matchedKeywords: string[];
  missingKeywords: string[];
  skillGaps: {
    skill: string;
    suggestion: string;
  }[];
  improvements: {
    section: string;
    suggestion: string;
  }[];
  overallFeedback: string;
}

interface ATSScorePreviewProps {
  analysis: ATSAnalysis;
  onApplyImprovements: () => void;
  onReject: () => void;
  isApplying?: boolean;
}

export function ATSScorePreview({
  analysis,
  onApplyImprovements,
  onReject,
  isApplying = false
}: ATSScorePreviewProps) {
  const T = useTranslation().t('ATSScorePreview', { returnObjects: true })

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return "Excellent Match";
    if (score >= 60) return "Good Match";
    if (score >= 40) return "Fair Match";
    return "Needs Improvement";
  };

  const getProgressColor = (score: number) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <Card className="bg-card/90 backdrop-blur-sm border-card-border mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Target className="h-5 w-5 text-primary" />
          {T["compatibilityAnalysis"]}
        </CardTitle>
        <CardDescription>
          {T["seeMatchRate"]}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Score Display */}
        <div className="text-center space-y-3">
          <div className={`text-5xl font-bold ${getScoreColor(analysis.score)}`}>
            {analysis.score}%
          </div>
          <Badge
            variant={analysis.score >= 60 ? "default" : "destructive"}
            className="text-sm px-4 py-1"
          >
            {getScoreLabel(analysis.score)}
          </Badge>
          <div className="relative pt-2">
            <Progress value={analysis.score} className="h-3" />
            <div
              className={`absolute top-2 h-3 rounded-full transition-all ${getProgressColor(analysis.score)}`}
              style={{ width: `${analysis.score}%` }}
            />
          </div>
        </div>

        {/* Overall Feedback */}
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="text-sm text-muted-foreground">{analysis.overallFeedback}</p>
        </div>

        <Accordion type="multiple" className="w-full">
          {/* Matched Keywords */}
          {analysis.matchedKeywords.length > 0 && (
            <AccordionItem value="matched">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>{T["matchedKeywords"]} ({analysis.matchedKeywords.length})</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-wrap gap-2 pt-2">
                  {analysis.matchedKeywords.map((keyword, index) => (
                    <Badge key={index} variant="secondary" className="bg-green-500/10 text-green-600 border-green-500/20">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Missing Keywords */}
          {analysis.missingKeywords.length > 0 && (
            <AccordionItem value="missing">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <span>{T["missingKeywords"]} ({analysis.missingKeywords.length})</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-wrap gap-2 pt-2">
                  {analysis.missingKeywords.map((keyword, index) => (
                    <Badge key={index} variant="outline" className="border-yellow-500/50 text-yellow-600">
                      {keyword}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {T["keywordSuggestion"]}
                </p>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Skill Gaps */}
          {analysis.skillGaps.length > 0 && (
            <AccordionItem value="gaps">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-blue-500" />
                  <span>{T["skillgapAnalysis"]}({analysis.skillGaps.length})</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  {analysis.skillGaps.map((gap, index) => (
                    <div key={index} className="bg-blue-500/5 rounded-lg p-3 border border-blue-500/20">
                      <p className="font-medium text-sm text-blue-600">{gap.skill}</p>
                      <p className="text-xs text-muted-foreground mt-1">{gap.suggestion}</p>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Improvements */}
          {analysis.improvements.length > 0 && (
            <AccordionItem value="improvements">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <span>{T["suggestedImprovements"]} ({analysis.improvements.length})</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  {analysis.improvements.map((improvement, index) => (
                    <div key={index} className="bg-primary/5 rounded-lg p-3 border border-primary/20">
                      <p className="font-medium text-sm">{improvement.section}</p>
                      <p className="text-xs text-muted-foreground mt-1">{improvement.suggestion}</p>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-border">
          <Button
            onClick={onApplyImprovements}
            disabled={isApplying}
            className="flex-1"
          >
            {isApplying ? (
              "Applying..."
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                {T["applyImprovements"]}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={onReject}
            disabled={isApplying}
            className="flex-1"
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            {T["skip&continue"]}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}